import * as React from "react";

import { Container, Box, Typography, Link as MUILink, List, ListItem, Divider, Chip, Stack, Breadcrumbs } from "@mui/material";

/**
 * ReportWerks Privacy Policy Page (MUI v6)
 * - Drop this into your Next.js app (pages/privacy.tsx or app route component)
 * - If not using Next.js, remove <Head> and export as a standard component.
 * - Update contact info and provider lists as needed.
 */

const Section = ({ id, title, children }) => (
  <Box component="section" id={id} sx={{ scrollMarginTop: { xs: 80, md: 100 } }}>
    <Typography variant="h5" fontWeight={700} gutterBottom>
      {title}
    </Typography>
    <Box sx={{ color: "text.secondary" }}>{children}</Box>
  </Box>
);

export default function PrivacyPolicyPage() {
  const updated = React.useMemo(() => {
    // Ensure the date stays stable at build time if SSR; otherwise adjust as needed.
    return new Intl.DateTimeFormat(undefined, { year: "numeric", month: "long", day: "2-digit" }).format(new Date("2025-09-24"));
  }, []);

  const anchor = (href, label) => (
    <MUILink href={href} underline="hover" color="inherit">
      {label}
    </MUILink>
  );

  return (
    <>


      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 } }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Chip label="Policy" size="small" />
          <Typography variant="overline" color="text.secondary">
            Last updated: {updated}
          </Typography>
        </Stack>

        <Typography variant="h3" fontWeight={800} gutterBottom>
          Privacy Policy
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          At ReportWerks, we value your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard your data when you use our website, services, and connected integrations.
        </Typography>

        <Breadcrumbs aria-label="policy sections" sx={{ mb: 4 }}>
          {anchor("#info-we-collect", "Information We Collect")}
          {anchor("#how-we-use", "How We Use Information")}
          {anchor("#sharing", "Data Sharing & Disclosure")}
          {anchor("#security", "Data Security")}
          {anchor("#retention", "Data Retention")}
          {anchor("#choices", "Your Choices")}
          {anchor("#children", "Children’s Privacy")}
          {anchor("#changes", "Changes to This Policy")}
          {anchor("#contact", "Contact Us")}
        </Breadcrumbs>

        <Section id="info-we-collect" title="Information We Collect">
          <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2 }}>
            1) Information You Provide
          </Typography>
          <List sx={{ listStyleType: "disc", pl: 4 }}>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Account details such as your name, email address, and organization.</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Information entered into forms or dashboards within ReportWerks.</ListItem>
          </List>

          <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2 }}>
            2) Automatically Collected Information (Analytics & Cookies)
          </Typography>
          <Typography sx={{ mb: 1 }}>
            When you visit our website or use our services, we may automatically collect device and usage information to improve your experience. This may include:
          </Typography>
          <List sx={{ listStyleType: "disc", pl: 4 }}>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Device, OS, and browser type</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>IP address and general location</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Referring/exit pages and URL parameters</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Date/time of visits and session duration</ListItem>
          </List>
          <Typography>
            We may use tools such as Google Analytics or similar services. You can manage cookies through your browser settings or opt out using provider tools. Data is aggregated and used for product improvement and security monitoring.
          </Typography>

          <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2 }}>
            3) OAuth and Connected Accounts
          </Typography>
          <Typography sx={{ mb: 1 }}>
            When you connect third-party platforms (e.g., Google Ads, Meta Ads, LinkedIn, Constant Contact, Bing), you authorize ReportWerks to access specific data via OAuth. We:
          </Typography>
          <List sx={{ listStyleType: "disc", pl: 4 }}>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Request limited, scoped access tokens; we do not store your third‑party passwords.</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Use tokens solely to fetch reporting/analytics data and show it in your dashboards.</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Securely store tokens and, where applicable, refresh them automatically to maintain service.</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Respect provider terms and scopes; you can revoke access at any time in ReportWerks or the provider’s security console.</ListItem>
          </List>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="how-we-use" title="How We Use Your Information">
          <List sx={{ listStyleType: "disc", pl: 4 }}>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Provide, maintain, and improve our reporting services.</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Display integrated marketing and fundraising data in your dashboards.</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Authenticate your account and authorize access to connected services.</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Communicate about updates, features, or support inquiries.</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Monitor system performance, detect abuse, and ensure security.</ListItem>
          </List>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="sharing" title="Data Sharing & Disclosure">
          <Typography sx={{ mb: 1 }}>
            We do not sell, rent, or trade your personal data or connected account information. We may share limited data with:
          </Typography>
          <List sx={{ listStyleType: "disc", pl: 4 }}>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Service providers that support analytics, hosting, or customer support (under confidentiality obligations).</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Law enforcement or regulators when required to comply with legal obligations.</ListItem>
          </List>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="security" title="Data Security">
          <Typography>
            We use industry‑standard security practices including HTTPS/TLS encryption in transit, role‑based access, auditing, and secure token storage. No method of transmission or storage is 100% secure; we continuously improve safeguards and recommend regularly reviewing your OAuth permissions.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="retention" title="Data Retention">
          <List sx={{ listStyleType: "disc", pl: 4 }}>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Website analytics data is retained in aggregate form for trend analysis.</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>OAuth tokens and fetched reporting data are retained while your account is active or until you revoke access.</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>You may request deletion of your account and associated data at any time.</ListItem>
          </List>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="choices" title="Your Choices">
          <List sx={{ listStyleType: "disc", pl: 4 }}>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Cookies & Analytics: manage via your browser settings or provider opt‑out tools.</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>OAuth Access: disconnect third‑party integrations in ReportWerks or at the provider’s security dashboard.</ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Communications: unsubscribe from marketing emails via the link in the message.</ListItem>
          </List>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="children" title="Children’s Privacy">
          <Typography>
            ReportWerks is not directed toward individuals under the age of 16, and we do not knowingly collect data from children.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="changes" title="Changes to This Policy">
          <Typography>
            We may update this Privacy Policy periodically. Significant changes will be communicated via email or a prominent notice on our website.
          </Typography>
        </Section>

        <Divider sx={{ my: 4 }} />

        <Section id="contact" title="Contact Us">
          <Typography sx={{ mb: 1 }}>If you have questions about this Privacy Policy or how your information is handled, contact us:</Typography>
          <List sx={{ listStyleType: "disc", pl: 4 }}>
            <ListItem sx={{ display: "list-item", py: 0.5 }}><strong>ReportWerks</strong></ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Email: <MUILink href="mailto:support@reportwerks.com">support@reportwerks.com</MUILink></ListItem>
            <ListItem sx={{ display: "list-item", py: 0.5 }}>Website: <MUILink href="https://www.reportwerks.com" target="_blank" rel="noopener">www.reportwerks.com</MUILink></ListItem>
          </List>
        </Section>
      </Container>
    </>
  );
}
